import { ReactNode, useEffect, useMemo, useRef, useState } from "react";

interface IProps {
  children: ReactNode;
}

const SMARTPHONE_WIDTH = 430; // "похожий" на ширину телефона контейнер
const MOBILE_BREAKPOINT = 520;

function safeIsInIFrame() {
  try {
    return window.self !== window.top;
  } catch {
    // Если доступ к window.top запрещён — считаем что мы внутри iframe.
    return true;
  }
}

export function DeviceFrame(props: IProps) {
  const outerRef = useRef<HTMLDivElement | null>(null);
  const dragStateRef = useRef<{
    dragging: boolean;
    startX: number;
    startY: number;
    startScrollTop: number;
    startScrollLeft: number;
    pointerId?: number;
  }>({
    dragging: false,
    startX: 0,
    startY: 0,
    startScrollTop: 0,
    startScrollLeft: 0,
  });

  const [useIframeMode, setUseIframeMode] = useState<boolean>(() => {
    if (typeof window === "undefined") return false;
    return window.innerWidth > MOBILE_BREAKPOINT && !safeIsInIFrame();
  });

  const srcForIframe = useMemo(() => {
    try {
      return window.location.pathname + window.location.search + window.location.hash;
    } catch {
      return "/";
    }
  }, []);

  const [scale, setScale] = useState<number>(1);

  useEffect(() => {
    const recalc = () => {
      const w = window.innerWidth;
      const s = Math.min(1, SMARTPHONE_WIDTH / w);
      setScale(s);
      setUseIframeMode(w > MOBILE_BREAKPOINT && !safeIsInIFrame());
    };

    recalc();
    window.addEventListener("resize", recalc);
    return () => window.removeEventListener("resize", recalc);
  }, []);

  const onWheel = (e: React.WheelEvent) => {
    const outer = outerRef.current;
    if (!outer) return;

    const target = e.target as HTMLElement | null;
    if (target) {
      const interactive = target.closest(
        "input, textarea, select, button, a, [role='button'], [contenteditable='true']"
      );
      if (interactive) return;
    }

    // Если нативный скролл не срабатывает (часто бывает с transform: scale),
    // прокидываем wheel вручную.
    const dy = e.deltaY;
    const dx = e.deltaX;

    // Не мешаем зуму/горизонтальному скроллу трекпадов.
    // Если SHIFT зажат — считаем что пользователь хочет горизонтальный скролл.
    if (Math.abs(dx) > 0.0001) {
      outer.scrollLeft += dx;
    }

    if (e.shiftKey) {
      outer.scrollLeft += dy;
    } else if (Math.abs(dy) > 0.0001) {
      outer.scrollTop += dy;
    }

    // Берём управление на себя, чтобы поведение было одинаковым на тач/мышь.
    e.preventDefault();
  };

  const onMouseDown = (e: React.MouseEvent) => {
    if (e.button !== 0) return; // только левая кнопка

    const target = e.target as HTMLElement | null;
    if (!target) return;

    // Не ломаем клики по интерактивным элементам.
    const interactive = target.closest(
      "input, textarea, select, button, a, [role='button'], [contenteditable='true']"
    );
    if (interactive) return;

    const outer = outerRef.current;
    if (!outer) return;

    dragStateRef.current.dragging = true;
    dragStateRef.current.startX = e.clientX;
    dragStateRef.current.startY = e.clientY;
    dragStateRef.current.startScrollTop = outer.scrollTop;
    dragStateRef.current.startScrollLeft = outer.scrollLeft;

    // Чтобы не выделялся текст во время перетаскивания.
    e.preventDefault();
  };

  useEffect(() => {
    const onMouseMove = (e: MouseEvent) => {
      if (!dragStateRef.current.dragging) return;
      const outer = outerRef.current;
      if (!outer) return;

      const dx = e.clientX - dragStateRef.current.startX;
      const dy = e.clientY - dragStateRef.current.startY;

      // Перетаскиваем "как карту": двигаем мышь вправо => контент едет влево (scrollLeft уменьшается).
      outer.scrollLeft = dragStateRef.current.startScrollLeft - dx;
      outer.scrollTop = dragStateRef.current.startScrollTop - dy;
    };

    const onMouseUp = () => {
      dragStateRef.current.dragging = false;
    };

    window.addEventListener("mousemove", onMouseMove);
    window.addEventListener("mouseup", onMouseUp);
    return () => {
      window.removeEventListener("mousemove", onMouseMove);
      window.removeEventListener("mouseup", onMouseUp);
    };
  }, []);

  if (useIframeMode) {
    return (
      <div className="deviceFrameOuter">
        <iframe
          className="deviceFrameIframe"
          title="Mobile app"
          src={srcForIframe}
          style={{
            width: `${SMARTPHONE_WIDTH}px`,
            height: "100vh",
            border: "none",
            background: "#fff",
          }}
        />
      </div>
    );
  }

  return (
    <div
      className="deviceFrameOuter"
      ref={outerRef}
      onWheel={onWheel}
      onMouseDown={onMouseDown}
    >
      <div
        className="deviceFrameInner"
        style={{
          transform: `scale(${scale})`,
          transformOrigin: "top left",
        }}
      >
        {props.children}
      </div>
    </div>
  );
}

